import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AdminHeader from '../components/AdminHeader';
import ClientHeader from '../components/ClientHeader';
import NavSidebar from '../components/NavSidebar';
import { usePageMeta } from '../components/DashboardShell';
import { useView } from '../contexts/ViewContext';
import DrillOver from '../components/shared/DrillOver';
import GenericCard from '../components/shared/GenericCard';
import PanelErrorBoundary from '../components/shared/PanelErrorBoundary';
import { ErrorState } from '../components/shared/StateMessage';
import { confidenceOpacity, titleCase } from '../components/shared/format';
import {
  askAnswer, curate, getDrill, getPage, getPlace, recordOutcome, recordRequest,
} from '../api/localIntelligence';
import AskConsole from '../components/local-intelligence/ask/AskConsolePanel';
import AskPanelSection from '../components/local-intelligence/ask/AskPanelSection';
import LocalDrillBody from '../components/local-intelligence/ask/LocalDrillBody';
import RightNow from '../components/local-intelligence/RightNowPanel';
import HelpRail from '../components/local-intelligence/HelpRailPanel';
import AroundMap from '../components/local-intelligence/AroundMapPanel';
import OpenTonight from '../components/local-intelligence/OpenTonightPanel';
import OccasionTiles from '../components/local-intelligence/OccasionTilesPanel';
import RequestsWorklist from '../components/local-intelligence/RequestsWorklistPanel';
import ClosedAndNew from '../components/local-intelligence/ClosedAndNewPanel';
import StayPlan from '../components/local-intelligence/StayPlan';
import StatTile from '../components/local-intelligence/StatTile';
import VerdictBanner from '../components/local-intelligence/verdict/VerdictBanner';
import {
  StatTilesSkeleton, AskPanelSkeleton, RightNowSkeleton, HelpRailSkeleton,
  OccasionsSkeleton, RequestsSkeleton, StayPlanSkeleton, ResultsSkeleton,
} from '../components/local-intelligence/skeleton/PanelSkeletons';
import {
  propertyTimeOf, mealNeedKeyForHour, recommendedWhenFor, augmentTimeOptions,
} from '../components/local-intelligence/liTime';
import { LocalActionsProvider } from '../components/local-intelligence/LocalActions';
import { buildMorningSheetHtml, openPrint } from '../components/local-intelligence/printSheets';

// Local Intelligence — "Around the Park". The front desk's answer to "what should I
// tell this guest, right now?": the three places to send them, the sentence to say,
// and the button to act. One fn_li_page call returns every panel with its sentence;
// nothing on this page is computed or worded in the browser.
//
// Composed layout (§2.4.0a): the verdict, five stat tiles, then the Ask console as the
// face with Help right now permanently beside it, then Right now, the map with Open
// tonight, the occasions, and last the booking requests with closures and new places.
// Any panel the database marks salient moves up into the attention slot under the
// tiles. One DrillOver serves every drill, wired the way Search Console wires its own.
//
// Structure: pure time logic lives in liTime.js, the loading skeletons in
// skeleton/PanelSkeletons.js, the stat tile in StatTile.js, the Local Insight strip
// in verdict/VerdictBanner.js, and the combined panel's layout shell in
// ask/AskPanelSection.js. This file owns data loading, the ask/drill controllers,
// and the layout composition.
const KNOWN = new Set(['ask', 'triage', 'right_now', 'local_map', 'open_now', 'occasions', 'requests', 'changes']);
const REFRESH_MS = 5 * 60 * 1000;

// Item 2 (work-page-fixes) — the guest-ready count has no pre-computed field in the
// fn_li_page response; the database writes the number into the verdict sentence
// ("…471 places within a 20-minute drive, and 403 are ready for guests."). The tile
// reads it out with this regex (C6: signal matching, never an exact string). If the
// wording ever changes the tile hides rather than show a wrong number — deriving
// from local_map pins would undercount (the map caps pins at 400 of 2965 shown).
const GUEST_READY_RE = /(\d[\d,]*)\s+are ready for guests/;

// Item 2 — how many answers to fetch from fn_li_answer in one request. The API
// takes p_limit (see api/localIntelligence.js askAnswer) and returns up to
// p_limit answers plus a separate `total` field (the full match count, shown
// in the results header as "N places"). The DISPLAY page size is a different
// constant — ANSWER_PAGE_SIZE = 3 in LocalDrillBody — and pages client-side.
// Previously every ask hardcoded limit: 3, so the API only ever returned the
// top 3 answers: the header read "60 places" (from result.total) while the
// footer read "Showing 1–3 of 3 places" (from answers.length). Fetching a
// generous batch here lets the client-side pager page through the full set, so
// answers.length matches result.total and the two counts agree. If the API
// caps p_limit below the true total, the footer will still undercount — that
// is a backend cap to raise (or add p_offset pagination), not a frontend fix.
const ANSWER_FETCH_LIMIT = 200;

// work-page-fixes Item 2 (page level) — friendly, generic error copy for any
// fn_li_page failure (timeout, network error, etc.). The raw Postgres error
// string (e.g. "Local Intelligence: canceling statement due to statement
// timeout") never reaches the user — only this fixed message renders, with the
// existing "Try again" button (ErrorState's onRetry bumps retryNonce to re-fire
// getPage). Same pattern as STAY_PLAN_ERROR in StayPlan.js. "narrow the window"
// is omitted here because a full page load has no window to narrow.
const PAGE_ERROR = 'This is taking longer than expected. Please try again.';

// Item 2 — loading starts true so the results-area skeleton shows from the moment
// fn_li_page resolves until the auto-select meal's fn_li_answer response arrives
// (see the right column of the combined panel). runAsk keeps it true while a call
// is in flight and sets it false when the answer lands; the retry effect resets it
// (and re-arms auto-select) on client switch / retry.
const INITIAL_ASK = { need: null, needKey: null, party: {}, timeIndex: 0, limit: ANSWER_FETCH_LIMIT, loading: true, error: null, result: null };

const NotAvailable = ({ name, reason }) => (
  <div className="w-full bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
    <svg className="w-12 h-12 mx-auto text-[#32B1FF] mb-4" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Around the Park is not available for this property</h2>
    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {name ? `"${name}" ${reason}` : `The selected project ${reason}`} Switch to another property in the header to load it.
    </p>
  </div>
);

let frameSeq = 0;
const nextId = () => { frameSeq += 1; return `f${frameSeq}`; };

export default function LocalIntelligencePage() {
  usePageMeta({ headerActive: 'Around the Park', sidebarActive: 'local-intel' });
  const view = useView();
  const username = localStorage.getItem('username') || 'User';
  const clientId = view.supabaseClientId;
  const clientDisplayName = view.clientDisplayName;
  const HeaderComp = view.isClient ? ClientHeader : AdminHeader;

  const [page, setPage] = useState({ loading: false, error: null, data: null });
  const [retryNonce, setRetryNonce] = useState(0);
  const [verdictDismissed, setVerdictDismissed] = useState(false);
  const [ask, setAsk] = useState(INITIAL_ASK);
  const [drillStack, setDrillStack] = useState([]);
  // Item 1 — which need the right column is answering. null shows the Right now
  // defaults; a set value shows that need's answer results in the same slot.
  const [activeNeed, setActiveNeed] = useState(null);
  const inputRef = useRef(null);
  const askSeq = useRef(0);
  // Item 1 — guards the one-time auto-select of the meal tile on first load so
  // it never re-fires when the When filter changes or a tile is clicked.
  const autoSelectedRef = useRef(false);
  // PART D — the (needKey, When label) pair the guardrail has already moved
  // away from (or the user has manually insisted on). While it matches, the
  // guardrail stays silent — the desk's choice wins; a different need or When
  // re-arms it. (work-page-fixes Item 2 — the visible "Adjusted to … for …"
  // hint label was removed; the override/shift behavior remains.)
  const whenOverrideRef = useRef(null);

  // ── Load the page; re-read it quietly every five minutes so "now", "tonight" and
  //    the open counts stay true on a screen left open all day ────────────────────
  useEffect(() => {
    setAsk(INITIAL_ASK);
    setDrillStack([]);
    setVerdictDismissed(false);
    // work-page-fixes Item 4 — a fresh load re-arms the guardrail (the override
    // belonged to the old page).
    whenOverrideRef.current = null;
    // Item 2 — re-arm the one-time auto-select so the meal tile auto-fires again
    // when a new client's data lands (client switch / retry). Without this, the
    // ref stays true from the first load and auto-select never re-fires, which
    // would leave ask.loading (initially true) stuck on and the results skeleton
    // showing forever after a client switch.
    autoSelectedRef.current = false;
    if (!clientId) { setPage({ loading: false, error: null, data: null }); return undefined; }
    // Item 1 (work-page-fixes) — cancel the in-flight fn_li_page when this effect
    // re-runs. React StrictMode unmounts in dev, firing the effect twice; without
    // the abort both calls run concurrently (~8.3s each) and both can hit the
    // statement timeout (code 57014). Aborting the first on cleanup leaves only
    // the second mount's request, matching production's single call.
    const controller = new AbortController();
    let cancelled = false;
    setPage({ loading: true, error: null, data: null });
    getPage(clientId, controller.signal)
      .then((data) => { if (!cancelled) setPage({ loading: false, error: null, data }); })
      .catch((err) => {
        // An AbortError is the intentional cancel above — don't flash the error
        // UI. Any other failure still sets the error state as before.
        if (err?.name === 'AbortError') return;
        if (!cancelled) setPage({ loading: false, error: PAGE_ERROR, data: null });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [clientId, retryNonce]);

  const refreshQuietly = useCallback(() => {
    if (!clientId) return;
    getPage(clientId).then((data) => setPage((p) => ({ ...p, data }))).catch(() => {});
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return undefined;
    const t = setInterval(refreshQuietly, REFRESH_MS);
    return () => clearInterval(t);
  }, [clientId, refreshQuietly]);

  const data = page.data;
  const panels = useMemo(() => {
    const list = Array.isArray(data?.panels) ? [...data.panels] : [];
    return list.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  }, [data]);
  const byKey = useMemo(() => Object.fromEntries(panels.map((p) => [p.panel, p])), [panels]);
  const partyOptions = useMemo(() => (Array.isArray(byKey.ask?.facts?.party_options) ? byKey.ask.facts.party_options : []), [byKey]);
  // work-page-fixes Item 1 — the property's wall-clock time (see propertyTimeOf
  // in liTime.js) and the raw as_of string when the API sends a full ISO
  // timestamp. asOf drives the "Now" pill's display and the meal auto-select;
  // asOfIso is what "Now" sends as p_at — the property's data time, never a
  // browser-generated stamp.
  const asOf = useMemo(() => propertyTimeOf(data?.as_of, data?.as_of_label), [data]);
  const asOfIso = useMemo(
    () => (typeof data?.as_of === 'string' && data.as_of.includes('T') ? data.as_of : null),
    [data],
  );
  // work-page-fixes Item 4 — augmentTimeOptions (liTime.js) synthesizes the two
  // missing lunch slots ("Today 12 PM" after "Now", "Tomorrow 12 PM" after
  // "Tomorrow 8 AM") into the database's time_options. The augmented list is
  // passed to AskConsole so the rendered pills and the page's timeIndex math
  // (runAsk, resolveWhenForNeed) always share one list — the rendered index
  // and the sent index can never drift.
  const timeOptions = useMemo(
    () => augmentTimeOptions(byKey.ask?.facts?.time_options, asOfIso),
    [byKey, asOfIso],
  );

  // work-page-fixes Item 4 — the When index an ask should run with for this
  // need: the recommended option (recommendedWhenFor in liTime.js) when the
  // (need, When) pair conflicts and the user hasn't insisted on that pair, else
  // the given index. On a shift it arms the override for the pair it moved away
  // from (so re-picking that option is respected — PART D).
  const resolveWhenForNeed = useCallback((needKey, timeIndex) => {
    const fromLabel = timeOptions[timeIndex]?.label;
    const rec = recommendedWhenFor(needKey, fromLabel, asOf.getHours());
    const ov = whenOverrideRef.current;
    if (!rec || (ov && ov.needKey === needKey && ov.label === fromLabel)) {
      return timeIndex;
    }
    const idx = timeOptions.findIndex((o) => o.label === rec);
    if (idx === -1 || idx === timeIndex) return timeIndex;
    whenOverrideRef.current = { needKey, label: fromLabel };
    return idx;
  }, [timeOptions, asOf]);

  // ── `/` focuses the Ask input from anywhere on the page ──────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      e.preventDefault();
      if (inputRef.current) inputRef.current.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Asking ───────────────────────────────────────────────────────────────────────
  const runAsk = useCallback((next) => {
    if (!clientId || !next.need) { setAsk(next); return; }
    const seq = askSeq.current + 1;
    askSeq.current = seq;
    setAsk({ ...next, loading: true, error: null });
    // work-page-fixes Item 1 — "Now" sends the property's as_of timestamp when
    // the API provides a full ISO one; otherwise the option's own at (the
    // database's stamp, never the browser's clock). Dated options always send
    // their own fixed at.
    const opt = timeOptions[next.timeIndex];
    const at = opt?.label === 'Now' && asOfIso ? asOfIso : opt?.at;
    askAnswer(clientId, { need: next.need, at, party: next.party, limit: next.limit })
      .then((result) => { if (askSeq.current === seq) setAsk((a) => ({ ...a, loading: false, result })); })
      .catch((e) => { if (askSeq.current === seq) setAsk((a) => ({ ...a, loading: false, error: e?.message || 'Could not answer that.' })); });
  }, [clientId, timeOptions, asOfIso]);

  // work-page-fixes Item 4 — every ask path runs the meal/When conflict
  // guardrail (resolveWhenForNeed): a clashing When shifts to the recommended
  // option; a clean pair stays put.
  const onAsk = ({ need, needKey }) => {
    const timeIndex = resolveWhenForNeed(needKey, ask.timeIndex);
    runAsk({ ...ask, need, needKey, timeIndex, limit: ANSWER_FETCH_LIMIT });
  };
  const onParty = (party) => { runAsk({ ...ask, party, limit: ANSWER_FETCH_LIMIT }); };
  // work-page-fixes Item 4 (revised) — honor an explicit When pick verbatim:
  // the pill the user clicked is the pill that runs. Instead of remapping the
  // When to fit the active meal (the old guardrail, which turned an 8 AM click
  // into "Tomorrow 7 PM" under Dinner), let the MEAL follow the time. The
  // time-of-day of the chosen When implies a meal — 8 AM → breakfast, 12 PM →
  // lunch, 7 PM → dinner, "Now" → the property's current meal
  // (mealNeedKeyForHour). When that meal differs from the active need, the
  // need (and the highlighted tile via setActiveNeed) switches so the query
  // reads sensibly (e.g. breakfast at 8 AM). Unknown labels keep the current
  // need. The need-driven paths (onAsk / askNeed / auto-select) still use
  // resolveWhenForNeed, so clicking a meal tile still auto-picks its When.
  const mealForWhen = (whenLabel, propertyHour) => {
    const t = String(whenLabel || '');
    if (/8\s*AM/i.test(t)) return 'breakfast';
    if (/12\s*PM/i.test(t)) return 'lunch';
    if (/7\s*PM/i.test(t)) return 'dinner';
    if (t === 'Now') return mealNeedKeyForHour(propertyHour);
    return null;
  };
  const onTime = (timeIndex) => {
    const meal = mealForWhen(timeOptions[timeIndex]?.label, asOf.getHours());
    if (meal && meal !== ask.needKey) {
      setActiveNeed(meal);
      runAsk({ ...ask, need: meal, needKey: meal, timeIndex, limit: ANSWER_FETCH_LIMIT });
      return;
    }
    runAsk({ ...ask, timeIndex, limit: ANSWER_FETCH_LIMIT });
  };
  // Item 1 — Clear resets EVERYTHING: the need tile selection (activeNeed →
  // null, which returns the results area to its empty state) plus the party
  // chips and When option. need/needKey are cleared too so runAsk skips the
  // fetch (see runAsk's `!next.need` early return) instead of re-fetching the
  // old need with the reset filters. The guardrail's override resets too.
  const onClear = () => {
    setActiveNeed(null);
    whenOverrideRef.current = null;
    runAsk({ ...ask, need: null, needKey: null, party: {}, timeIndex: 0, limit: ANSWER_FETCH_LIMIT });
  };

  const askNeed = (need) => {
    if (!need) return;
    const timeIndex = resolveWhenForNeed(need, ask.timeIndex);
    runAsk({ ...ask, need, needKey: need, timeIndex, limit: ANSWER_FETCH_LIMIT });
    setActiveNeed(need);
    const el = document.getElementById('panel-ask');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // work-page-fixes Item 1 — on first load, auto-select the meal tile for the
  // PROPERTY's time of day (as_of, not the browser clock — mealNeedKeyForHour in
  // liTime.js) so the combined panel opens with that meal's results immediately.
  // The ref guard makes this run exactly once; later When-filter changes or manual
  // tile clicks update `ask`/`activeNeed` through their own handlers and do not
  // re-trigger this effect.
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!data) return;
    autoSelectedRef.current = true;
    const mealKey = mealNeedKeyForHour(asOf.getHours());
    // work-page-fixes Item 4 — the default When ("Now") gets the same conflict
    // check as any manual pick: a morning Dinner opens on "Tonight 7 PM"
    // instead.
    const timeIndex = resolveWhenForNeed(mealKey, ask.timeIndex);
    setActiveNeed(mealKey);
    runAsk({ ...ask, need: mealKey, needKey: mealKey, timeIndex, limit: ANSWER_FETCH_LIMIT });
  }, [data, runAsk, ask, asOf, resolveWhenForNeed]);

  // ── Drilling (Search Console's wiring, keyed by frame id so a late answer can
  //    never overwrite a different frame) ──────────────────────────────────────────
  const setFrame = (id, patch) => setDrillStack((s) => s.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const popDrill = useCallback(() => setDrillStack((s) => s.slice(0, -1)), []);
  const closeDrill = useCallback(() => setDrillStack([]), []);

  const openPlace = (entityId, name) => {
    if (!entityId) return;
    const id = nextId();
    const load = () => {
      setFrame(id, { loading: true, error: false });
      getPlace(clientId, entityId)
        .then((detail) => setFrame(id, { loading: false, label: detail?.name || name || 'Place', detail }))
        .catch((e) => setFrame(id, { loading: false, error: true, label: 'Could not load this place', message: e?.message, retry: load }));
    };
    setDrillStack((s) => [...s, { id, kind: 'place', label: name || 'Loading…', rows: [], loading: true }]);
    load();
  };

  const openList = (what, value) => {
    const id = nextId();
    const load = () => {
      setFrame(id, { loading: true, error: false });
      getDrill(clientId, what, value ?? null, 50, 0)
        .then((res) => setFrame(id, { loading: false, label: titleCase(res?.label || 'Places'), rows: res?.rows || [], total: res?.total ?? (res?.rows || []).length, what, value }))
        .catch((e) => setFrame(id, { loading: false, error: true, label: 'Could not load these records', message: e?.message, retry: load }));
    };
    setDrillStack((s) => [...s, { id, kind: 'list', label: 'Loading…', rows: [], loading: true, what, value }]);
    load();
  };

  const pushDrill = (drill) => {
    if (!drill?.fn) return;
    const p = drill.params || {};
    if (drill.fn === 'fn_li_answer') askNeed(p.p_need);
    else if (drill.fn === 'fn_li_place_detail') openPlace(p.p_entity_id);
    else if (drill.fn === 'fn_li_drill') openList(p.p_what, p.p_value);
  };

  const loadMore = (frame) => {
    setFrame(frame.id, { loadingMore: true });
    getDrill(clientId, frame.what, frame.value ?? null, 50, (frame.rows || []).length)
      .then((res) => setFrame(frame.id, { loadingMore: false, rows: [...(frame.rows || []), ...(res?.rows || [])], total: res?.total ?? frame.total }))
      .catch(() => setFrame(frame.id, { loadingMore: false }));
  };

  const onOpen = (item) => { if (item?.drill) pushDrill(item.drill); else if (item?.entity_id) openPlace(item.entity_id, item.name); };
  // ── Query results render in the right column of the combined panel (Item 3).
  //    The page owns the answer list and its j/k keyboard nav. "Show more" is gone
  //    — the list is frontend-paginated 3 per page (see LocalDrillBody /
  //    useClientPagination). The page still computes the full `answers` set so
  //    keyboard nav (j/k + Enter) can reach every card.
  // Item — no card is highlighted by default; the first j/k press selects card 0.
  const [answerActiveIdx, setAnswerActiveIdx] = useState(null);
  const answers = Array.isArray(ask.result?.answers) ? ask.result.answers : [];
  useEffect(() => { setAnswerActiveIdx(null); }, [ask.result]);
  const onAnswerListKey = (e) => {
    if (answers.length === 0) return;
    if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); setAnswerActiveIdx((i) => i == null ? 0 : Math.min(i + 1, answers.length - 1)); }
    if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); setAnswerActiveIdx((i) => i == null ? 0 : Math.max(i - 1, 0)); }
    if (e.key === 'Enter') { if (answerActiveIdx == null) return; e.preventDefault(); onOpen(answers[answerActiveIdx]); }
  };

  const pickPlace = (p) => ({
    entity_id: p.entity_id, name: p.name, phone: p.phone || p.actions?.call || null, line: p.line || null,
    hours_label: p.hours_label || null, address: p.address || null, category_label: p.category_label || null,
    staff: p.staff || null, recommendation_id: p.recommendation_id || null,
  });
  const onCall = (place) => {
    if (!place?.entity_id) return;
    setDrillStack((s) => [...s, { id: nextId(), kind: 'call', label: `Call ${place.name || ''}`.trim(), rows: [], place: pickPlace(place) }]);
  };
  const onSend = (place) => {
    if (!place?.entity_id) return;
    setDrillStack((s) => [...s, { id: nextId(), kind: 'send', label: `Send ${place.name || 'this place'} to a guest`, rows: [], place: pickPlace(place) }]);
  };
  const onBook = (place) => {
    if (!place?.entity_id) return;
    setDrillStack((s) => [...s, { id: nextId(), kind: 'request', label: `Book ${place.name || ''}`.trim(), rows: [], place: { entity_id: place.entity_id, name: place.name } }]);
  };

  // ── Staff actions ────────────────────────────────────────────────────────────────
  const onCurate = async (entityId, action, value) => {
    const res = await curate(clientId, entityId, action, value);
    if (res?.staff) {
      setDrillStack((s) => s.map((f) => (f.kind === 'place' && f.detail?.entity_id === entityId ? { ...f, detail: { ...f.detail, staff: res.staff } } : f)));
    }
    refreshQuietly();
    return res;
  };

  const onRecordRequest = async (requestId, outcome) => {
    const res = await recordRequest(clientId, requestId, outcome);
    refreshQuietly();
    return res;
  };

  const onOutcome = (recommendationId, outcome) => {
    recordOutcome(clientId, recommendationId, outcome).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn('[local-intelligence] outcome not recorded', e);
    });
  };

  const parkName = byKey.local_map?.facts?.park?.name || clientDisplayName || '';
  const actions = { onCall, onSend };
  const drillCtx = {
    onBook,
    onOutcome,
    parkName,
    onCurate,
    onRecordRequest,
    onRequestCreated: refreshQuietly,
    onOpenPlace: (entityId, name) => openPlace(entityId, name),
    onOpenDrill: pushDrill,
    onLoadMore: loadMore,
    defaultPartySize: typeof ask.party?.group === 'number' ? ask.party.group : 2,
    defaultAt: timeOptions[ask.timeIndex]?.at,
  };

  // Item 1 — the need-filtered panel header reflects the active When option. The "Now"
  // chip (and a missing option) reads "Open right now"; any other chip reads its own
  // label (e.g. "Tonight 7 PM"). Passed through to LocalDrillBody so the header always
  // says what the user is looking at: "Need · <whenLabel> · N places".
  const whenLabel = (() => {
    const lbl = timeOptions[ask.timeIndex]?.label;
    return !lbl || lbl === 'Now' ? 'Open right now' : lbl;
  })();

  // work-page-fixes Item 1 - align the results header count with the pagination
  // total. LocalDrillBody renders the header count from result.total (the
  // API-reported count from fn_li_answer) and the pagination footer from the
  // client-side answers array length (useClientPagination -> totalItems). When
  // the API returns fewer answers than it reports total, the two disagree
  // (e.g. header "161 places" vs footer "Showing 1-4 of 103 places"). Override
  // result.total to the actual answers array length before handing the query
  // down, so the header ("N places") and the footer ("Showing 1-M of N places")
  // share one source of truth - what the client actually paginates. The backend
  // still overcounts (reports 161 but returns 103); that is a fn_li_answer issue
  // to raise separately, not a frontend fix.
  const answersQuery = ask.result
    ? { ...ask, result: { ...ask.result, total: answers.length } }
    : ask;

  // ── Panels ───────────────────────────────────────────────────────────────────────
  const renderPanel = (panel) => {
    if (!panel) return null;
    if (!KNOWN.has(panel.panel) || panel.confidence === 'insufficient') {
      return <GenericCard key={panel.panel} panel={panel} title={panel.title} sentence={panel.sentence} />;
    }
    const fade = confidenceOpacity(panel.confidence);
    const body = (() => {
      switch (panel.panel) {
        case 'local_map': return <AroundMap panel={panel} answerPins={answers} onOpen={onOpen} onOpenDrill={pushDrill} />;
        case 'open_now': return <OpenTonight panel={panel} onOpen={onOpen} onOpenDrill={pushDrill} />;
        case 'occasions': return <OccasionTiles panel={panel} onOpenDrill={pushDrill} />;
        case 'requests': return <RequestsWorklist panel={panel} onRecord={onRecordRequest} onOpenPlace={openPlace} onOpenDrill={pushDrill} />;
        case 'changes': return <ClosedAndNew panel={panel} onOpenDrill={pushDrill} />;
        default: return null;
      }
    })();
    return <div key={panel.panel} className={fade}>{body}</div>;
  };

  const salient = panels.filter((p) => (p.salience || 0) > 0 && !['ask', 'triage'].includes(p.panel));
  const salientKeys = new Set(salient.map((p) => p.panel));
  const inPlace = (key) => (salientKeys.has(key) ? null : renderPanel(byKey[key]));
  const unknown = panels.filter((p) => !KNOWN.has(p.panel));

  const openNow = byKey.open_now;
  const requests = byKey.requests;
  const changes = byKey.changes;
  // Item 2 — places ready to send guests, read from the verdict sentence (see
  // GUEST_READY_RE above); null when the verdict is missing or reworded, which
  // hides the tile instead of showing a stale or wrong count.
  const guestReadyMatch = typeof data?.verdict === 'string' ? data.verdict.match(GUEST_READY_RE) : null;
  const guestReadyCount = guestReadyMatch ? Number(guestReadyMatch[1].replace(/,/g, '')) : null;
  const overdue = (requests?.facts?.rows || []).some((r) => r.overdue);
  const top = drillStack[drillStack.length - 1];

  const loaded = !page.loading && !page.error && data;
  const usable = loaded && data.status === 'ok' && panels.length > 0;

  return (
    <LocalActionsProvider value={actions}>
    <>
        <main className="flex-1 min-w-0">
          <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">
            {/* ─── Page header — title left, as-of opposite (Weather's header) ─── */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Around the Park</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Where to send guests right now — places to eat, things to do, and help nearby.
                </p>
              </div>
              {/* work-page-fixes Item 1 — the "As of" timestamp sits above the
                  "Print morning sheet" button on the right of the header. No
                  container box: they stack as plain right-aligned text + a small
                  brand-tinted action button (light #32B1FF/10 background, #32B1FF
                  text + border — the same subtle brand-fill treatment
                  CompetitorProfilesPanel uses) so it reads as a primary daily
                  action without a heavy solid fill. The printer glyph is an inline
                  Feather/Lucide SVG (no icon library is installed — same approach
                  as StayPlan's Glyph/MealIcon and HelpRail), matching the
                  icon+text pattern used by every other button on the page. */}
              {usable && (
                <div className="flex flex-col items-end gap-1.5">
                  {data.as_of_label && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">As of {data.as_of_label}</p>
                  )}
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20 transition-colors"
                    onClick={() => openPrint(buildMorningSheetHtml({ parkName, page: data }))}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M6 9V2h12v7" />
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" rx="1" />
                    </svg>
                    Print morning sheet
                  </button>
                </div>
              )}
            </div>

            {!clientId && <NotAvailable name={clientDisplayName} reason="does not have Around the Park set up." />}
            {/* Per-panel loading skeletons (work-page-fixes Item 1, now in
                skeleton/PanelSkeletons.js) — each panel renders its own placeholder
                that mimics its real layout instead of one page-level skeleton. */}
            {clientId && page.loading && (
              <>
                <StatTilesSkeleton />
                <AskPanelSkeleton />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                  <RightNowSkeleton />
                  <HelpRailSkeleton />
                </div>
                <StayPlanSkeleton />
                <OccasionsSkeleton />
                <RequestsSkeleton />
              </>
            )}
            {clientId && !page.loading && page.error && (
              <ErrorState title="Could not load Around the Park" message={page.error} variant="card" onRetry={() => setRetryNonce((n) => n + 1)} />
            )}
            {clientId && loaded && !usable && <NotAvailable name={clientDisplayName} reason="has no local places mapped yet." />}

            {usable && (
              <>
                {/* ─── Tier 1 — the verdict ─── */}
                {data.verdict && !verdictDismissed && (
                  <VerdictBanner verdict={data.verdict} onDismiss={() => setVerdictDismissed(true)} />
                )}

                {/* ─── Tier 2 — five stat tiles, each opens its list ─── */}
                <PanelErrorBoundary label="tiles">
                  <div className="grid [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))] gap-4">
                    <StatTile
                      color="brandBlue" icon="target" title="Within 20 min"
                      tooltip="Places to eat, things to do and services within a 20-minute drive."
                      value={openNow?.facts?.within_20_min}
                      unit="places nearby"
                      onOpen={() => openList('drive_band', '20')}
                    />
                    <StatTile
                      color="emerald" icon="clock" title={openNow?.title || 'Open tonight'}
                      tooltip="Places within a 20-minute drive that are open at the time shown."
                      value={openNow?.facts?.open_within_20_min}
                      unit="open places"
                      onOpen={() => (openNow?.facts?.drill ? pushDrill(openNow.facts.drill) : openList('open_now', '20'))}
                    />
                    {guestReadyCount != null && (
                      <StatTile
                        color="emerald" icon="share" title="Ready to send guests"
                        tooltip="Places the desk can send guests to without checking first — verified hours, phone and directions."
                        value={guestReadyCount}
                        unit="places ready"
                        onOpen={() => openList('guest_ready', null)}
                      />
                    )}
                    <StatTile
                      color={overdue ? 'red' : 'amber'} icon="checklist" title="Booking requests"
                      tooltip="Bookings the desk is arranging for guests that still need a call or an answer."
                      value={requests?.sample_size}
                      unit="requests"
                      onOpen={() => openList('requests', 'all')}
                    />
                    <StatTile
                      color="red" icon="eyeOff" title="Closed places"
                      tooltip="Places Google lists as closed. They are already left out of every answer."
                      value={changes?.sample_size}
                      unit="within 45-minute drive"
                      onOpen={() => openList('closed', null)}
                    />
                  </div>
                </PanelErrorBoundary>

                {/* ─── Attention slot — any panel the database marks salient ─── */}
                {salient.length > 0 && (
                  <PanelErrorBoundary label="attention">
                    <div className="space-y-4">{salient.map(renderPanel)}</div>
                  </PanelErrorBoundary>
                )}

                {/* ─── Tier 3 — the face: one full-width seamless panel (layout shell
                     in ask/AskPanelSection.js). The LEFT column holds the title/subtext
                     then the AskConsole filter content; the RIGHT column holds the
                     answer results or, when no need is selected, a short empty-state
                     prompt. Both columns always render so clicking a need fills the
                     right column in place without reflowing the left column. ─── */}
                <AskPanelSection
                  filters={(
                    <PanelErrorBoundary label="ask">
                      <AskConsole
                        panel={byKey.ask}
                        timeOptions={timeOptions}
                        ask={ask}
                        inputRef={inputRef}
                        activeNeed={activeNeed}
                        asOf={asOf}
                        onAsk={onAsk}
                        onParty={onParty}
                        onTime={onTime}
                        onClear={onClear}
                        onNeedSelect={setActiveNeed}
                      />
                    </PanelErrorBoundary>
                  )}
                  results={(
                    <PanelErrorBoundary label="answers">
                      {ask.loading ? (
                        /* Item 2 — results-area skeleton while fn_li_answer is in
                           flight. ask.loading starts true (INITIAL_ASK) so the
                           skeleton shows from the moment fn_li_page resolves —
                           covering the auto-select gap on initial page load — and
                           stays until the auto-selected meal's answers arrive. */
                        <ResultsSkeleton />
                      ) : activeNeed ? (
                        <LocalDrillBody
                          query={answersQuery}
                          rightNow={byKey.right_now}
                          mapPanel={byKey.local_map}
                          whenLabel={whenLabel}
                          onOpenDrill={pushDrill}
                          onOpen={onOpen}
                          onBook={onBook}
                          onOutcome={onOutcome}
                          onAnswerListKey={onAnswerListKey}
                          answerActiveIdx={answerActiveIdx}
                          onBack={() => setActiveNeed(null)}
                        />
                      ) : (
                        /* Item 3 — empty state for the results column when no need
                           is selected (auto-select covers most loads; this is the
                           fallback for cleared filters / bypassed auto-select). */
                        <div className="flex flex-col items-center justify-center text-center min-h-[12rem] gap-3">
                          <svg className="w-6 h-6 text-gray-300 dark:text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                          </svg>
                          <p className="text-sm text-gray-400 dark:text-gray-500 max-w-xs">
                            Select a need or ask a question to see nearby places.
                          </p>
                        </div>
                      )}
                    </PanelErrorBoundary>
                  )}
                />

                {/* ─── Tier 4 — Nearby Places that are Open Right Now beside Help right
                     now, side by side at 50/50 (Item 2). Stay Plan renders directly
                     below this row. ─── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                  <div className="min-w-0 h-full flex flex-col [&>*]:flex-1">
                    <PanelErrorBoundary label="right_now">
                      {/* Closed right now renders as a slim footer strip inside this card
                          (work-page-fixes Item 1). Pass null when changes is salient so
                          it renders in the attention slot instead (no double render). */}
                      {byKey.right_now && (
                        <RightNow
                          panel={byKey.right_now}
                          openNowCount={byKey.open_now?.facts?.open_within_20_min}
                          onOpen={onOpen}
                          onAskNeed={(row) => askNeed(row?.drill?.params?.p_need || row?.need_key)}
                          onBook={onBook}
                          onOutcome={onOutcome}
                          closedNowPanel={salientKeys.has('changes') ? null : byKey.changes}
                          onOpenDrill={pushDrill}
                        />
                      )}
                    </PanelErrorBoundary>
                  </div>
                  <div className="min-w-0 h-full flex flex-col [&>*]:flex-1">
                    <PanelErrorBoundary label="triage">
                      {byKey.triage && <HelpRail panel={byKey.triage} onAskNeed={(row) => askNeed(row?.drill?.params?.p_need || row?.need_key)} onOpenDrill={pushDrill} />}
                    </PanelErrorBoundary>
                  </div>
                </div>

                {/* ─── Plan a stay — always-expanded full-width section ─── */}
                <div className="w-full">
                  <StayPlan
                    clientId={clientId}
                    asOf={data.as_of}
                    partyOptions={partyOptions}
                    initialParty={ask.party}
                    onOpen={onOpen}
                    onBook={onBook}
                    onOutcome={onOutcome}
                  />
                </div>

                <PanelErrorBoundary label="occasions">{inPlace('occasions')}</PanelErrorBoundary>

                {/* ─── Tier 5 — the record ─── */}
                <PanelErrorBoundary label="requests">{inPlace('requests')}</PanelErrorBoundary>

                {/* §2.4.9 — a panel this release does not know renders as a GenericCard */}
                {unknown.length > 0 && (
                  <PanelErrorBoundary label="unknown">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{unknown.map(renderPanel)}</div>
                  </PanelErrorBoundary>
                )}
              </>
            )}
          </div>
        </main>

      {drillStack.length > 0 && (
        <DrillOver
          stack={drillStack}
          onBack={popDrill}
          onClose={closeDrill}
          loading={Boolean(top?.loading)}
          renderBody={(frame) => <LocalDrillBody frame={frame} clientId={clientId} ctx={drillCtx} />}
        />
      )}
    </>
    </LocalActionsProvider>
  );
}
